import { MapPin } from 'lucide-react';

import { useCustomer } from '@/hooks/useCustomer';

/**
 * LocationPicker — lets a MASTER account (a customer with more than one
 * location) choose which location an order is for.
 *
 * Renders NOTHING for a normal "1:1 account" (a single location): per the
 * product decision there is no "choose a location" step for normal accounts,
 * so single-location accounts see no picker noise at all.
 */
export function LocationPicker({
  selectedLocationId,
  setSelectedLocationId,
  className,
}: {
  selectedLocationId: string | null;
  setSelectedLocationId: (id: string | null) => void;
  className?: string;
}) {
  const { data: ctx } = useCustomer();
  const locations = ctx?.locations ?? [];

  // 1:1 account (or not yet loaded): no picker.
  if (locations.length <= 1) return null;

  return (
    <label
      className={
        'inline-flex items-center gap-2 rounded-xl border border-brix-border bg-brix-surface px-3 py-2 shadow-card ' +
        (className ?? '')
      }
    >
      <MapPin size={15} className="text-brix-primary shrink-0" aria-hidden />
      <span className="text-xs font-semibold tracking-tight text-brix-muted shrink-0">
        Delivering to
      </span>
      <select
        value={selectedLocationId ?? ''}
        onChange={(e) => setSelectedLocationId(e.target.value || null)}
        className="bg-transparent text-sm font-semibold text-brix-ink tracking-tight outline-none cursor-pointer max-w-[16rem]"
        aria-label="Choose delivery location"
      >
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.displayName}
          </option>
        ))}
      </select>
    </label>
  );
}
